import {
  type Country,
  createUrlResolver,
  type Locale,
  type Language,
  siteConfig,
  siteNames,
  getLanguageFromLocale,
} from '@seek/melways-sites';

import {
  type Environment,
  type DevContext,
  type EmployerSiteName,
  type ConfigOptions,
  ConfigItemKey,
} from 'src/types';

// example config
// =======================================================
// {
//   version: '35',
//   locale: 'en-AU',
//   environment: 'staging',
//   features: { 'feature-a': true, 'feature-b': false },
// };

export const createConfig = ({
  site,
  locale,
  environment,
  appVersion,
  isProd,
  devContext,
  isTrackingEnabled,
  isCypressTest,
}: ConfigOptions) => ({
  // const features = setFeatureToggles(environment);
  [ConfigItemKey.VERSION]: appVersion,
  [ConfigItemKey.SITE]: site,
  [ConfigItemKey.LOCALE]: locale,
  [ConfigItemKey.ENV]: environment,
  [ConfigItemKey.IS_PROD]: isProd,
  [ConfigItemKey.DEV_CONTEXT]: devContext,
  [ConfigItemKey.IS_TRACKING_ENABLED]: isTrackingEnabled,
  [ConfigItemKey.IS_CYPRESS_TEST]: isCypressTest,

  // [ConfigItemKey.FEATURES]: features,
});

type ConfigItemFallback = any;

export const getConfigItem = (
  configItem: ConfigItemKey,
  fallback: ConfigItemFallback,
) => {
  if (typeof window !== 'undefined' && window.__config) {
    const configValue = (window.__config as any)[configItem];
    if (configValue === undefined) {
      return fallback;
    }
    return configValue;
  }
  return fallback;
};

export const getEnvironment = (): Environment =>
  getConfigItem(ConfigItemKey.ENV, 'development');

export const getSite = (): EmployerSiteName => {
  const site = getConfigItem(ConfigItemKey.SITE, siteNames.employer.seek.au);
  if (site === null) {
    throw new Error('Unable to get site from config');
  }
  return site;
};
export const getLocale = (): Locale => {
  const locale = getConfigItem(ConfigItemKey.LOCALE, 'en-AU');
  if (locale === null) {
    throw new Error('Unable to get locale from config');
  }
  return locale;
};

export const getLanguage = (): Language => getLanguageFromLocale(getLocale());

export const isLocalDev = (): boolean => getEnvironment() === 'development';

export const isStaging = (): boolean => getEnvironment() === 'staging';

export const isProd = (): boolean => getEnvironment() === 'production';

export const urlResolver = createUrlResolver({
  site: getSite(),
  language: getLocale(),
  staging: getEnvironment() === 'staging',
});

export const getDevContext = (): DevContext | null =>
  getConfigItem(ConfigItemKey.DEV_CONTEXT, null);

export const isTrackingEnabled = (): boolean =>
  getConfigItem(ConfigItemKey.IS_TRACKING_ENABLED, false);

export const getAppVersion = (): string =>
  getConfigItem(ConfigItemKey.VERSION, 'local');

export const getCountryFromSite = (site: EmployerSiteName): Country =>
  siteConfig[site].classification.country;
